import { Box, Flex, Spacer, Skeleton, Heading } from '@chakra-ui/react';
import React, { useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { CoreBorrowingBaseInsightsGqlDataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { PortfolioChartDatePicker } from '../shared/PortfolioChartDatePicker';
import {
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
  defaultSelectedTimePeriod,
  formatChartDataByReportingDate,
} from '../shared/portfolioChartUtils';
import { PortfolioTimePeriodSelectButtons } from '../shared/PortfolioTimePeriodSelectButtons';
import { PortfolioSummaryChart } from './PortfolioSummaryChart';
import { PortfolioSummaryChartCards } from './PortfolioSummaryChartCards';
import { colors } from 'packages/common-frontend/colors';
import { HighlightBox } from 'packages/common-frontend/components/HighlightBox';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
} from 'packages/common-frontend/constants';
import { useGetDailyCoreBorrowingBaseInsightsQuery } from 'packages/portfolio/requests/requests.gql.generated';

export const PortfolioSummaryChartContainer: React.FC<{
  creditFacilityId: string;
}> = ({ creditFacilityId }) => {
  const [
    selectedBar,
    setSelectedBar,
  ] = useState<CoreBorrowingBaseInsightsGqlDataGQLType | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    CoreBorrowingBaseInsightsGqlDataGQLType[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    CoreBorrowingBaseInsightsGqlDataGQLType[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const {
    refetch: refetchBorrowingBaseInsightsData,
    data: borrowingBaseInsightsData,
    loading: borrowingBaseInsightsLoading,
  } = useGetDailyCoreBorrowingBaseInsightsQuery({
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      // we must make sure to set the core date range chart data with all of the data by day
      // this ensures that we have the base by-day data ready to use
      // when going back to a higher-frequency time period view than the currently selected period
      const formattedChartData = formatChartDataByReportingDate({
        chartData: data.getDailyCoreBorrowingBaseInsights,
        selectedTimePeriod: ChartTimePeriod.day,
      });
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate({
          chartData: data.getDailyCoreBorrowingBaseInsights,
          selectedTimePeriod,
        });
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);
    },
    notifyOnNetworkStatusChange: true,
  });

  if (borrowingBaseInsightsLoading || !borrowingBaseInsightsData) {
    return <Skeleton height="600px" />;
  }

  const onSelectDateRange = (newDateRange: [Date | null, Date | null]) => {
    const [newStartDate, newEndDate] = newDateRange;

    if (newStartDate && newEndDate) {
      refetchBorrowingBaseInsightsData({
        creditFacilityParams: {
          creditFacilityId,
          reportingDateStart: newStartDate
            ? commonDate.dateToFullDateString(newStartDate)
            : getDefaultDashboardDisplayStartDate(),
          reportingDateEnd: newEndDate
            ? commonDate.dateToFullDateString(newEndDate)
            : getDefaultDashboardDisplayEndDate(),
        },
      });
      setChartDataDateRange({
        chartDataStartDate: newStartDate,
        chartDataEndDate: newEndDate,
      });
    }

    // if dates cleared, pass in the initial reporting date conditions
    if (!newStartDate && !newEndDate) {
      refetchBorrowingBaseInsightsData({
        creditFacilityParams: {
          creditFacilityId,
          reportingDateStart: getDefaultDashboardDisplayStartDate(),
          reportingDateEnd: getDefaultDashboardDisplayEndDate(),
        },
      });
      setChartDataDateRange({
        chartDataStartDate: chartsInitialStartDateJs,
        chartDataEndDate: chartsInitialEndDateJs,
      });
    }
  };

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate({
      chartData: chartDataForDateRange,
      selectedTimePeriod: timePeriod,
    });

    setChartDataWithTimePeriod(formattedChartData);
  };

  const onSelectBar = (
    selectedBarData: CoreBorrowingBaseInsightsGqlDataGQLType,
  ) => {
    setSelectedBar(selectedBarData);
  };

  return (
    <HighlightBox backgroundColor={colors.millet[50]}>
      <Heading as="h3" fontSize="24px" mb="24px">
        Funding Overview
      </Heading>
      <Box
        backgroundColor="white"
        borderRadius="8px"
        borderWidth="1px"
        borderColor={colors.gray[200]}
        padding="40px"
        mb="40px"
      >
        <Flex>
          <PortfolioChartDatePicker
            onSelectDateRange={onSelectDateRange}
            currentDateRange={chartDataDateRange}
          />
          <Spacer />
          <PortfolioTimePeriodSelectButtons
            currentSelectedTimePeriod={selectedTimePeriod}
            onSelectTimePeriod={onSelectTimePeriod}
          />
        </Flex>
        <Box mt="40px">
          <PortfolioSummaryChart
            chartData={chartDataWithTimePeriod}
            selectedTimePeriod={selectedTimePeriod}
            currentSelectedBarReportingDate={
              selectedBar
                ? selectedBar.reportingDate
                : getDefaultDashboardDisplayEndDate()
            }
            onSelectBar={onSelectBar}
          />
        </Box>
      </Box>
      {selectedBar && (
        <PortfolioSummaryChartCards
          selectedBarData={selectedBar}
          creditFacilityId={creditFacilityId}
        />
      )}
    </HighlightBox>
  );
};
